import { StyleSheet, Platform } from 'react-native';
import type { Theme } from '../../../src/theme/getTheme';

export interface CreateWorldCupGroupScreenStyleOptions {
  contentBottomInset?: number;
}

export const getCreateWorldCupGroupScreenStyles = (
  theme: Theme,
  options?: CreateWorldCupGroupScreenStyleOptions,
) => {
  const bottomInset = options?.contentBottomInset ?? 0;

  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.background.primary,
    },
    gradientBackdrop: {
      ...StyleSheet.absoluteFillObject,
    },
    bokehLarge: {
      position: 'absolute',
      width: 280,
      height: 280,
      borderRadius: 140,
      backgroundColor: theme.primary,
      opacity: 0.18,
      top: -80,
      alignSelf: 'center',
      left: '50%' as never,
      marginLeft: -140,
    },
    bokehSmall: {
      position: 'absolute',
      width: 140,
      height: 140,
      borderRadius: 70,
      backgroundColor: theme.accentLight,
      opacity: 0.1,
      bottom: 200,
      left: -40,
    },
    bokehAccent: {
      position: 'absolute',
      width: 80,
      height: 80,
      borderRadius: 40,
      backgroundColor: theme.primary,
      opacity: 0.07,
      top: 320,
      right: -20,
    },
    keyboardAvoid: {
      flex: 1,
      ...(Platform.OS === 'web' && {
        height: '100%' as never,
      }),
    },
    inner: {
      flex: 1,
      flexDirection: 'column',
      zIndex: 1,
      ...(Platform.OS === 'web' && {
        maxWidth: 480,
        width: '100%',
        alignSelf: 'center' as const,
      }),
    },

    // ─── Fixed header ────────────────────────────────────────────────
    header: {
      flexShrink: 0,
      paddingHorizontal: 20,
      paddingTop: 8,
      paddingBottom: 10,
    },
    navRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      marginBottom: 10,
    },
    progressTrack: {
      flex: 1,
      height: 5,
      borderRadius: 5,
      backgroundColor: theme.gray[200],
      overflow: 'hidden',
    },
    progressFill: {
      height: '100%',
      borderRadius: 5,
      backgroundColor: theme.primary,
    },
    progressFillStep1: {
      width: '33%' as never,
    },
    progressFillStep2: {
      width: '67%' as never,
    },
    backButton: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      paddingVertical: 6,
      paddingLeft: 2,
      paddingRight: 8,
    },
    backButtonPressed: { opacity: 0.65 },
    backButtonHover: { opacity: 0.82 },
    backButtonText: {
      fontSize: 14,
      fontWeight: '600',
      color: theme.primary,
    },
    stepCounter: {
      fontSize: 12,
      fontWeight: '700',
      color: theme.primary,
      marginBottom: 4,
      letterSpacing: 0.5,
    },
    stepCounterTotal: {
      fontSize: 12,
      fontWeight: '500',
      color: theme.gray[500],
    },
    title: {
      fontSize: 26,
      fontWeight: '800',
      color: theme.gray[900],
      marginBottom: 4,
      letterSpacing: -0.3,
    },
    subtitle: {
      fontSize: 13,
      fontWeight: '400',
      color: theme.gray[500],
      lineHeight: 18,
    },

    // ─── Scrollable content ──────────────────────────────────────────
    scrollArea: {
      flex: 1,
      backgroundColor: 'transparent',
    },
    scrollContent: {
      paddingHorizontal: 20,
      paddingTop: 4,
      paddingBottom: 96,
    },

    // ─── Sticky CTA ──────────────────────────────────────────────────
    stickyCtaWrap: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: bottomInset,
      paddingHorizontal: 16,
      paddingTop: 28,
    },
    stickyCtaFade: {
      ...StyleSheet.absoluteFillObject,
    },
    stickyCtaButton: {
      borderRadius: 16,
      overflow: 'hidden',
    },
    stickyCtaDisabled: {
      opacity: 0.4,
    },
    stickyCtaPressed: { opacity: 0.88 },
    stickyCtaGradient: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 15,
      gap: 8,
    },
    stickyCtaText: {
      fontSize: 15,
      fontWeight: '800',
      color: theme.onPrimary,
      letterSpacing: 0.2,
    },
    stickyCtaSublabelWrap: {
      paddingHorizontal: 8,
      paddingVertical: 3,
      borderRadius: 99,
      backgroundColor: 'rgba(0,0,0,0.25)',
    },
    stickyCtaSublabelText: {
      fontSize: 11,
      fontWeight: '700',
      color: theme.onPrimary,
      opacity: 0.85,
    },
  });
};

export type CreateWorldCupGroupScreenStyles = ReturnType<typeof getCreateWorldCupGroupScreenStyles>;
